import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import type {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
// The printouts' catalogue, adapted for the lab. It moves to the printouts, its real owner, when they
// adopt the picker; the picker itself never imports printouts.
import {getSamplePreviewContext} from "../../printouts/print/samplePreviewContext";
import {
    getPrintoutVariableDefaultFallback,
    getPrintoutVariablesForType,
} from "../../printouts/variables/printoutVariables";
import type {
    PrintoutVariableDefinition,
    PrintoutVariableKind,
    PrintoutVariableScope,
} from "../../printouts/variables/printoutVariables";
import {getValueAtPath, resolvePrintoutVariable} from "../../printouts/variables/printoutVariableResolver";
import type {PrintoutVariableResolveContext} from "../../printouts/variables/printoutVariableResolver";
import type {ResolveContext, VariableDefinition, VariableScopeIcon} from "../../common/newVariablePicker";

export const PRINTOUT_SCOPE_ICONS: Record<PrintoutVariableScope, VariableScopeIcon> = {
    hotel: ApartmentOutlinedIcon,
    guest: PersonOutlineIcon,
    reservation: EventOutlinedIcon,
    service: RoomServiceOutlinedIcon,
    serviceCategory: LocalOfferOutlinedIcon,
    sector: MapOutlinedIcon,
    resource: MeetingRoomOutlinedIcon,
    resourceCategory: CategoryOutlinedIcon,
    agenda: EventNoteOutlinedIcon,
    agendaItem: ScheduleOutlinedIcon,
    folio: ReceiptLongOutlinedIcon,
    folioItem: ReceiptOutlinedIcon,
    other: InfoOutlinedIcon,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isCurrencyCode = (value: unknown): value is string =>
    typeof value === "string" && /^[A-Z]{3}$/.test(value);

const toDate = (value: unknown): Date | null => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value !== "string" && typeof value !== "number") return null;

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
};

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * The day, as the core's formatter reads one.
 *
 * A bare day is taken as written, because parsing `2026-08-12` gives UTC midnight, which is the
 * previous day west of Greenwich. Anything else — a timestamp, or a datetime carrying an offset —
 * is read off the local clock, which is where the printouts read it too.
 */
const asDay = (value: unknown) => {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const date = toDate(value);
    if (!date) return undefined;

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Wall-clock `HH:mm`, from either a clock string or a timestamp. */
const asClockTime = (value: unknown) => {
    if (typeof value === "string") {
        const clock = value.match(/^(\d{1,2}):(\d{2})/);
        if (clock) return `${pad(Number(clock[1]))}:${clock[2]}`;
    }

    const date = toDate(value);
    if (!date) return undefined;

    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Printout money is a number or a money record; the core's formatter wants the amount alone. */
const asAmount = (value: unknown) => {
    if (typeof value === "number") return value;
    if (!isRecord(value)) return undefined;
    if (typeof value.amount === "number") return value.amount;
    if (typeof value.amountCents === "number") return value.amountCents / 100;

    return undefined;
};

/** A list-kind compute may answer with one name rather than a list of them; one name is a list. */
const asStringList = (value: unknown) => {
    if (typeof value === "string") return value ? [value] : [];
    if (!Array.isArray(value)) return undefined;

    return value
        .filter(item => typeof item === "string" || typeof item === "number")
        .map(String);
};

const printoutContext = (context: ResolveContext) => context as PrintoutVariableResolveContext;

const rawValueOf = (variable: PrintoutVariableDefinition, context: ResolveContext) =>
    resolvePrintoutVariable(variable.key, printoutContext(context))?.rawValue;

/** A printout raw value in the shape the core's formatter understands. */
export const normalizePrintoutRaw = (kind: PrintoutVariableKind, raw: unknown) => {
    switch (kind) {
        case "date":
            return asDay(raw);
        case "time":
            return asClockTime(raw);
        case "currency":
            return asAmount(raw);
        case "duration":
        case "number":
            return typeof raw === "number" ? raw : undefined;
        case "boolean":
            return typeof raw === "boolean" ? raw : undefined;
        case "list":
            return asStringList(raw);
        default:
            return raw;
    }
};

const rawFor = (variable: PrintoutVariableDefinition, context: ResolveContext) => {
    if (variable.attr.kind === "localizedText") {
        // The printouts' resolver flattens a localized map; read the map so the core picks the
        // language. A computed one has no map to read, and arrives already flattened.
        return variable.attr.path
            ? getValueAtPath(context, variable.attr.path)
            : rawValueOf(variable, context);
    }

    return normalizePrintoutRaw(variable.attr.kind, rawValueOf(variable, context));
};

/** The printouts' currency order: the variable's own path, the amount, the folio, then the hotel. */
const currencyFor = (variable: PrintoutVariableDefinition, context: ResolveContext) => {
    const named = variable.attr.currencyPath
        ? getValueAtPath(context, variable.attr.currencyPath)
        : undefined;
    if (isCurrencyCode(named)) return named;

    const raw = rawValueOf(variable, context);
    if (isRecord(raw) && isCurrencyCode(raw.currency)) return raw.currency;

    const folio = getValueAtPath(context, "folio.currency");
    if (isCurrencyCode(folio)) return folio;

    const hotel = getValueAtPath(context, "organization.currencyCode");

    return isCurrencyCode(hotel) ? hotel : undefined;
};

/**
 * The printouts' catalogue as the core's. Keys are the printouts' own and are never renamed.
 * A printout type narrows it to the scopes that type's print page can fill; none offers them all.
 */
export const printoutCatalogue = (printoutType?: PrintoutType | null): VariableDefinition[] =>
    getPrintoutVariablesForType(printoutType).map(variable => ({
        key: variable.key,
        label: variable.label,
        description: variable.description,
        scope: variable.scopeLabel,
        scopeKey: variable.scope,
        kind: variable.attr.kind,
        path: variable.attr.path,
        resolve: context => rawFor(variable, context),
        compute: variable.attr.compute,
        fallback: getPrintoutVariableDefaultFallback(variable),
        ...(variable.attr.kind === "currency"
            ? {currency: (context: ResolveContext) => currencyFor(variable, context)}
            : {}),
        icon: PRINTOUT_SCOPE_ICONS[variable.scope],
    }));

/**
 * The printouts' preview booking in the guest's language. Without a printout type every scope is
 * filled; with one, only what that type's print page would pass.
 */
export const printoutSampleContext = (language: string, printoutType?: PrintoutType | null): ResolveContext =>
    getSamplePreviewContext(printoutType, language) as ResolveContext;
